/**
 * Skeleton primitives — uniform loading placeholders so every athlete
 * page speaks the same visual language while it waits for data.
 *
 * Three shapes:
 *   • `<Skeleton />` — a single pulsing block (pass className for size).
 *   • `<SkeletonText lines={N} />` — N stacked lines, last one 2/3 width
 *     for the "paragraph that doesn't end on a perfect grid" feel.
 *   • `<SkeletonCard />` — a typical card shell (header strip + body).
 *
 * Pulse animation is the standard Tailwind `animate-pulse` so we don't
 * add a custom keyframe per component. `aria-hidden` everywhere — these
 * placeholders carry no information for a screen-reader.
 */
import type { ReactNode } from "react";


export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-pill bg-bg-raised ${className}`}
      aria-hidden="true"
    />
  );
}


export function SkeletonText({
  lines = 1,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-3 ${i === lines - 1 && lines > 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  );
}


export function SkeletonCard({
  className = "",
  children,
  label,
  testId,
}: {
  className?: string;
  children?: ReactNode;
  /**
   * Screen-reader-only label naming the section that's loading.
   * Cycle 18.8 — per-section skeletons mean per-section labels so users
   * with assistive tech hear "Loading readiness", not just "Loading".
   * Omit for generic placeholders.
   */
  label?: string;
  testId?: string;
}) {
  const ariaProps = label
    ? { role: "status" as const, "aria-live": "polite" as const, "aria-busy": true, "aria-label": label }
    : { "aria-hidden": true };
  return (
    <div
      className={`indigo-card p-4 space-y-2 ${className}`}
      data-testid={testId}
      {...ariaProps}
    >
      {children ?? (
        <>
          <Skeleton className="h-4 w-1/2" />
          <SkeletonText lines={2} />
        </>
      )}
    </div>
  );
}

/**
 * Today/Dashboard structural skeletons — match the actual section shapes
 * so the loading layout doesn't jump on data arrival.
 */
export function SkeletonReadinessRing({ label }: { label?: string }) {
  return (
    <SkeletonCard
      className="h-32 flex items-center justify-center"
      label={label}
      testId="skeleton-readiness-ring"
    >
      <div className="w-20 h-20 rounded-full bg-bg-raised animate-pulse" />
    </SkeletonCard>
  );
}

export function SkeletonSessionHero({ label }: { label?: string }) {
  return (
    <SkeletonCard className="h-40" label={label} testId="skeleton-session-hero">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-8 w-2/3" />
      <SkeletonText lines={2} />
    </SkeletonCard>
  );
}

export function SkeletonMiniWidget({ label }: { label?: string }) {
  return (
    <SkeletonCard className="h-32" label={label} testId="skeleton-mini-widget">
      <Skeleton className="h-3 w-1/4" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-2 w-1/2" />
    </SkeletonCard>
  );
}


/**
 * Заглушка ГРАФИКА — 2026-08-04.
 *
 * ЗАЧЕМ ОТДЕЛЬНАЯ. Карточки Анализа показывали на время загрузки строку
 * «Загружаю…» высотой сорок пикселей, а затем на её месте появлялся график
 * высотой в двести двадцать четыре. Каждая карточка толкала страницу вниз,
 * и замер это подтвердил: CLS 0.358 при «хорошо» до 0.1, из них 0.287 —
 * один прыжок на появлении графиков.
 *
 * Высота задаётся снаружи тем же классом, что у настоящего контейнера
 * (`h-56` у карточек страницы, 240px у кривой формы). Заглушка, которая
 * «примерно похожа», сдвиг не убирает — она обязана занимать РОВНО столько
 * же места.
 *
 * Столбики разной высоты, а не сплошной прямоугольник: сплошной блок на
 * четверть экрана читается как ошибка отрисовки, а не как ожидание.
 */
export function SkeletonChart({
  className = "h-56",
  label,
  testId = "skeleton-chart",
}: {
  className?: string;
  label?: string;
  testId?: string;
}) {
  // Высоты фиксированы, а не случайны: случайные меняются на каждой
  // перерисовке, и заглушка начинает мерцать.
  const bars = [42, 68, 55, 80, 62, 91, 74, 58, 86, 70, 95, 64];
  const ariaProps = label
    ? { role: "status" as const, "aria-live": "polite" as const, "aria-busy": true, "aria-label": label }
    : { "aria-hidden": true as const };
  return (
    <div
      className={`w-full flex items-end gap-1.5 ${className}`}
      data-testid={testId}
      {...ariaProps}
    >
      {bars.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-bg-raised animate-pulse"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

export function SkeletonList({
  rows = 3,
  variant = "card",
}: {
  rows?: number;
  variant?: "card" | "row";
}) {
  return (
    <div className="space-y-2" data-testid="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) =>
        variant === "card" ? (
          <SkeletonCard key={i} />
        ) : (
          <div
            key={i}
            className="flex items-center gap-3 p-2 rounded-pill bg-bg-raised animate-pulse"
          >
            <div className="w-10 h-10 rounded-full bg-bg-surface" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-1/3 bg-bg-surface rounded" />
              <div className="h-2 w-2/3 bg-bg-surface rounded" />
            </div>
          </div>
        ),
      )}
    </div>
  );
}
